import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, BellOff, BellRing, CheckCircle2, AlertTriangle, XCircle, Info,
  CheckCheck, ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotifications, useMarkAllRead, useMarkRead } from "@/hooks/useNotifications";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import type { Notification } from "@/types/database";

// ─── Icon / colour maps ────────────────────────────────────────────────────────

const iconMap: Record<string, any> = {
  success: CheckCircle2,
  error:   XCircle,
  warning: AlertTriangle,
  info:    Info,
};

const colorMap: Record<string, string> = {
  success: "text-green-500",
  error:   "text-destructive",
  warning: "text-amber-500",
  info:    "text-blue-500",
};

const borderMap: Record<string, string> = {
  success: "border-l-green-500",
  error:   "border-l-destructive",
  warning: "border-l-amber-500",
  info:    "border-l-blue-500",
};

// ─── Relative time helper ────────────────────────────────────────────────────

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now  = new Date();
  const diffMs  = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr  = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr  / 24);

  if (diffSec < 60)  return "Just now";
  if (diffMin < 60)  return `${diffMin}m ago`;
  if (diffHr  < 24)  return `${diffHr}h ago`;
  if (diffDay < 7)   return `${diffDay}d ago`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

// ─── Filter types ─────────────────────────────────────────────────────────────

type FilterTab = "all" | "unread" | "alerts" | "system";

function applyFilter(notifications: Notification[], tab: FilterTab): Notification[] {
  switch (tab) {
    case "unread":
      return notifications.filter((n) => !n.read);
    case "alerts":
      return notifications.filter((n) => n.type === "warning" || n.type === "error");
    case "system":
      return notifications.filter((n) => n.type === "info" || n.type === "success");
    default:
      return notifications;
  }
}

// ─── Notification row ─────────────────────────────────────────────────────────

function NotificationRow({ n, onRead }: { n: Notification; onRead: (id: string) => void }) {
  const navigate  = useNavigate();
  const Icon      = iconMap[n.type] || Info;
  const navLink   = n.data?.link as string | undefined;

  const handleClick = () => {
    if (!n.read) onRead(n.id);
    if (navLink) navigate(navLink);
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-4 cursor-pointer
        hover:bg-muted/40 transition-colors
        border-l-2
        ${!n.read ? borderMap[n.type] ?? "border-l-primary" : "border-l-transparent"}
        ${!n.read ? "bg-primary/[0.02]" : ""}
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      {/* Icon */}
      <span className={`mt-0.5 shrink-0 ${colorMap[n.type] ?? "text-info"}`}>
        <Icon className="h-4 w-4" />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium leading-tight">{n.title}</p>
          {!n.read && (
            <Badge className="h-4 px-1 text-[9px] bg-primary text-primary-foreground shrink-0">
              New
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{n.message}</p>
      </div>

      {/* Right column: time + deep-link indicator */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
          {formatRelativeTime(n.created_at)}
        </span>
        {navLink && (
          <ExternalLink className="h-3 w-3 text-muted-foreground/50" />
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Notifications() {
  const { data: notifications = [], isLoading } = useNotifications();
  const markAllRead = useMarkAllRead();
  const markRead    = useMarkRead();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();

  const [activeTab, setActiveTab] = useState<FilterTab>("all");

  const filtered   = applyFilter(notifications as Notification[], activeTab);
  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const tabCounts: Record<FilterTab, number> = {
    all:    notifications.length,
    unread: unreadCount,
    alerts: notifications.filter((n: any) => n.type === "warning" || n.type === "error").length,
    system: notifications.filter((n: any) => n.type === "info"    || n.type === "success").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
              : "You're all caught up!"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pushSupported && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => (pushSubscribed ? unsubscribePush.mutate() : subscribePush.mutate())}
              disabled={subscribePush.isPending || unsubscribePush.isPending}
              className="gap-1.5"
            >
              {pushSubscribed ? <BellRing className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
              {pushSubscribed ? "Push enabled" : "Enable push"}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadCount === 0}
            className="gap-1.5"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Mark all read
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FilterTab)}>
        <TabsList className="h-8">
          {(["all", "unread", "alerts", "system"] as FilterTab[]).map((tab) => (
            <TabsTrigger key={tab} value={tab} className="text-xs capitalize gap-1.5">
              {tab}
              {tabCounts[tab] > 0 && (
                <Badge variant="secondary" className="h-4 min-w-[18px] px-1 text-[9px]">
                  {tabCounts[tab]}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Notification list */}
      <Card>
        <CardContent className="divide-y divide-border p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <Bell className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">
                {activeTab === "all" ? "No notifications yet" : `No ${activeTab} notifications`}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activeTab === "all"
                  ? "Actions taken in the system will appear here."
                  : "Try switching to another tab."}
              </p>
            </div>
          ) : (
            filtered.map((n) => (
              <NotificationRow
                key={n.id}
                n={n}
                onRead={(id) => markRead.mutate(id)}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
